import { observable, action, runInAction, makeObservable, computed } from 'mobx';
import { TabModel } from '../../common/types/TabModel';
import { IotaSessionsStore } from '../../common/stores';
import { SessionStates } from '../../common/services/types';
import {
    CustomSessionChangeResult,
    CustomSessionState,
    CustomSessionResponseBase,
    CustomSessionRuntimeData,
    ISessionStoreBase
} from '../types';
import { Disposer } from '../../custom_shared/misc';
import CustomSessionService from '../services/CustomSessionService';

export default abstract class SessionStoreBase<TResponse extends CustomSessionResponseBase>
    extends Disposer
    implements ISessionStoreBase
{
    isInitializing: boolean = true;

    isLoading: boolean = false;

    inProgress: boolean = false;

    isFinished: boolean = false;

    response: TResponse | null = null;

    readonly sessionService = new CustomSessionService();

    private attemptId: string | null = null;
    private stateRevision: number = 0;
    private stateRequest: number = 0;

    constructor(
        protected readonly tab: TabModel,
        protected readonly iotaSessionsStore: IotaSessionsStore
    ) {
        super();

        makeObservable(this, {
            isInitializing: observable,
            isLoading: observable,
            inProgress: observable,
            isFinished: observable,
            response: observable,
            setIsLoading: action,
            setIsInitializing: action,
            initialize: action,
            getSession: action,
            continueSession: action,
            errors: computed,
            hasErrors: computed,
            errorsWithDetails: computed,
            isReady: computed
        });

        this.iotaSessionsStore.iotaSessionChanges.subscribe((session: CustomSessionChangeResult<TResponse>) => {
            if (this.sessionId === session.sessionId) {
                this.setData(session);
            }
        });
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setIsInitializing(isInitializing: boolean) {
        this.isInitializing = isInitializing;
    }

    setData(session: CustomSessionState<TResponse> | CustomSessionChangeResult<TResponse>, fromRead: boolean = false) {
        if (session.attemptId && session.stateRevision != null && session.stateRevision >= 0) {
            if (this.attemptId && session.attemptId !== this.attemptId && !fromRead) {
                // Attempt ids are opaque. Resolve a different attempt through the current
                // server snapshot instead of treating arrival order as attempt order.
                void this.getSession();
                return;
            }
            if (session.attemptId === this.attemptId && session.stateRevision <= this.stateRevision) {
                return;
            }
            this.attemptId = session.attemptId;
            this.stateRevision = session.stateRevision;
            if (!fromRead) {
                ++this.stateRequest;
            }
        } else if (this.attemptId) {
            return;
        }
        runInAction(() => {
            this.inProgress = session.inProgress;
            this.isFinished = !session.response.actions.length;
            this.response = session.response;

            // Session updates are received via push service when actions are executed.
            // Setting isLoading to false immediately after an action call would cause issues due to push service latency
            // (e.g., slow internet connections). By this time, the user may have already changed the app state,
            // leading to inconsistencies. Therefore, we set isLoading to false when the session data actually arrives or when action call throws an error.
            if (this.isLoading) {
                this.setIsLoading(false);
            }
        });
    }

    async initialize() {
        try {
            this.setIsInitializing(true);
            await this.getSession();
        } catch (err) {
            console.log(err);
        } finally {
            this.setIsInitializing(false);
        }
    }

    async getSession() {
        const request = ++this.stateRequest;
        try {
            this.setIsLoading(true);

            const response = await this.sessionService.getSessionState<TResponse>(
                this.sessionExtension,
                this.sessionId
            );

            if (response.isOk() && request === this.stateRequest) {
                response.map(session => this.setData(session, true));
            }
        } catch (err) {
            console.log(err);
        } finally {
            if (request === this.stateRequest) {
                this.setIsLoading(false);
            }
        }
    }

    async continueSession(actionId: string, runtimeData: CustomSessionRuntimeData) {
        try {
            this.setIsLoading(true);

            const result = await this.sessionService.continueSession(
                this.sessionExtension,
                this.sessionId,
                actionId,
                runtimeData
            );
            if (result.isErr()) {
                this.setIsLoading(false);
            }
        } catch (err) {
            console.log(err);
            this.setIsLoading(false);
        }
    }

    async startSession(applicationId: string, packageId: string, parameters: Record<string, unknown> | null = null) {
        return await this.sessionService.startSession(applicationId, packageId, parameters, this.sessionId);
    }

    async updateSession(sessionId: string, state: SessionStates) {
        return await this.sessionService.updateSession(sessionId, state);
    }

    async getFileFromIotaTempStorage(filePath: string) {
        await this.sessionService.getFileFromIotaTempStorage(filePath);
    }

    get sessionId() {
        return this.tab.metadata.sessionId as string;
    }

    get sessionExtension() {
        return this.tab.metadata.applicationData.appExtension as string;
    }

    get errors() {
        return this.response?.errors ?? [];
    }

    get hasErrors() {
        return this.errors.length > 0;
    }

    get errorsWithDetails() {
        return this.errors.map(error => `${error.message}: ${error.details}`);
    }

    get isReady() {
        return !this.isInitializing && !this.isLoading && !this.inProgress;
    }
}
